import { Phone, Mail, MapPin, MessageCircle, Clock, Download, FileText } from 'lucide-react';
import { FadeIn } from '@/components/Animations';
import ContactForm from '@/components/ContactForm';

const contactInfo = [
  {
    icon: Phone,
    label: 'Phone',
    value: '072 028 6584',
    href: 'tel:+27720286584',
  },
  {
    icon: Mail,
    label: 'Email',
    value: 'ask@gigfleet.co.za',
    href: 'mailto:ask@gigfleet.co.za',
  },
  {
    icon: MessageCircle,
    label: 'WhatsApp',
    value: 'Chat with us',
    href: 'https://wa.me/27720286584',
  },
  {
    icon: MapPin,
    label: 'Location',
    value: 'South Africa',
    href: null,
  },
];

const ContactPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 via-purple-50/30 to-white">
        <div className="container-wide">
          <FadeIn>
            <div className="max-w-4xl">
              <span className="inline-block px-4 py-2 bg-purple-100 text-primary-mlm rounded-full text-sm font-semibold tracking-wide uppercase mb-6">
                Contact Us
              </span>
              <h1 className="text-4xl md:text-6xl font-heading font-bold text-slate-900 mb-6">
                Let's <span className="text-primary-mlm">Connect</span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed">
                Have questions about #MyLifeMatters? Want to explore partnership opportunities? 
                We'd love to hear from you.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info */}
            <FadeIn>
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl font-heading font-bold text-slate-900 mb-4">
                    Get In Touch
                  </h2>
                  <p className="text-slate-600">
                    Reach out through any of the channels below, or fill out the form and 
                    we'll get back to you within 24 hours.
                  </p>
                </div>

                {/* Contact Cards */}
                <div className="grid grid-cols-2 gap-4">
                  {contactInfo.map((item) => (
                    <div key={item.label} className="bg-slate-50 rounded-2xl p-6 hover:shadow-lg transition-all">
                      <item.icon className="w-8 h-8 text-primary-mlm mb-4" />
                      <p className="text-sm text-slate-500 mb-1">{item.label}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          target={item.href.startsWith('http') ? '_blank' : undefined}
                          rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="font-semibold text-slate-900 hover:text-primary-mlm transition-colors"
                          data-testid={`contact-${item.label.toLowerCase()}`}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="font-semibold text-slate-900">{item.value}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Key Contact */}
                <div className="bg-gradient-to-br from-primary-mlm to-purple-700 rounded-2xl p-8 text-white">
                  <h3 className="font-semibold text-white mb-4">Primary Contact</h3>
                  <div className="space-y-2">
                    <p className="text-xl font-heading font-bold">Andrew Clarke</p>
                    <p className="text-purple-200">Founder & Director</p>
                    <div className="pt-4 space-y-2 text-purple-100">
                      <a href="tel:+27720286584" className="flex items-center gap-2 hover:text-white">
                        <Phone className="w-4 h-4" />
                        072 028 6584
                      </a>
                      <a href="mailto:ask@gigfleet.co.za" className="flex items-center gap-2 hover:text-white">
                        <Mail className="w-4 h-4" />
                        ask@gigfleet.co.za
                      </a>
                    </div>
                  </div>
                </div>

                {/* Response Time */}
                <div className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                  <Clock className="w-10 h-10 text-emerald-600" />
                  <div>
                    <p className="font-semibold text-slate-900">Quick Response</p>
                    <p className="text-sm text-slate-600">We typically respond within 24 hours</p>
                  </div>
                </div>

                {/* Download Brochure */}
                <div className="bg-slate-900 rounded-2xl p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center shrink-0">
                    <FileText className="w-6 h-6 text-purple-300" />
                  </div>
                  <div>
                    <p className="font-semibold text-white mb-1">Partnership Brochure</p>
                    <p className="text-sm text-slate-400 mb-3">
                      Download our partnership document for full details on how to get involved.
                    </p>
                    <a
                      href="/MyLifeMatters_Call_For_Partnership.pdf"
                      download
                      className="inline-flex items-center gap-2 bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full px-5 py-2.5 text-sm font-semibold transition-all"
                      data-testid="contact-download-pdf-btn"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </a>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Contact Form */}
            <FadeIn delay={0.2}>
              <div className="bg-slate-50 rounded-2xl p-8">
                <h3 className="text-2xl font-heading font-bold text-slate-900 mb-6">
                  Send Us a Message
                </h3>
                <ContactForm formType="general" showCompany={true} />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
